import React from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  message,
} from "antd";
import { UnlockOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";
import { FormComponent, BaseFormComponentProps } from "../../BaseComponent";
import { PartnerKeyTypeArray } from "../../../interfaces/IIntegration";
import { IntegrationConstants } from "../../../helpers/enums/Integration";
import {
  integrationCredentialCreate,
  integrationCredentialUpdate,
  integrationCredentialRevealSecret,
} from "../../../services/integration";

type RegisterCredentialProps = BaseFormComponentProps & {
  selectedItem: any;
  closeModal: () => void;

  shelters: any[] | null;
  loadShelters: (args?: any) => void;
  loadingShelters?: boolean
};

export default class RegisterCredentialForm extends FormComponent<RegisterCredentialProps> {
  private isEdit = !!this.props.selectedItem?.uuid;

  private form = React.createRef<any>();

  state = {
    saving: false,
    partnerKey: this.props.selectedItem?.partner_key || "",
    shelterUuid: this.props.selectedItem?.shelter?.uuid || "",

    // On create we always edit the credentials; on edit they stay hidden until
    // the user explicitly reveals them (which fetches the secrets).
    showCredentials: !this.isEdit,
    revealing: false,
    revealError: null as string | null,
  };

  private delayedSearch = debounce((v: string) => {
    if (v) {
      this.props.loadShelters({ name: { ilike: `%${v}%` } });
    }
  }, 500);

  private getCredentialsSchema(partnerKey: string) {
    return IntegrationConstants.CREDENTIALS_FIELDS.find(
      (p) => p.partner_key === partnerKey
    );
  }

  // Fetch the (decrypted) secrets and show the credential inputs. This can fail
  // when the current user is not allowed to reveal secrets.
  async revealCredentials() {
    this.setState({ revealing: true, revealError: null });

    try {
      const response = await integrationCredentialRevealSecret({
        credential_uuid: this.props.selectedItem.uuid,
      });

      const body = response.data as any;

      if (response.ok && body?.prefix === "2xx") {
        const revealed = body.data?.credentials ?? body?.data ?? {};

        this.setState({ showCredentials: true }, () => {
          // Set the values after the inputs mounted so they get populated
          this.form.current?.setFieldsValue({ credentials: revealed });
        });
      } else {
        const reason = body?.message || response.originalError?.message || response.problem || "unknown error"
        this.setState({
          revealError:
            "Could not load the credentials. " +
            (reason
              ? ` (${typeof reason === "string" ? reason : JSON.stringify(reason)})`
              : ""),
        });
      }
    } catch (e) {
      this.setState({
        revealError: "Could not load the credentials. Please try again.",
      });
    } finally {
      this.setState({ revealing: false });
    }
  }

  async handleSave(values: any) {
    const { partnerKey, shelterUuid, showCredentials } = this.state;

    this.setState({ saving: true });

    try {
      let response;

      if (this.isEdit) {
        const payload: any = {
          credential_uuid: this.props.selectedItem.uuid,
          description: values.description,
        };

        // Only send the credentials when the user actually revealed/edited them,
        // otherwise leave the stored secrets untouched.
        if (showCredentials) {
          payload.credentials = this.pickCredentials(partnerKey, values);
        }

        response = await integrationCredentialUpdate(payload);
      } else {
        response = await integrationCredentialCreate({
          shelter_uuid: shelterUuid,
          partner_key: partnerKey,
          description: values.description,
          credentials: this.pickCredentials(partnerKey, values),
        });
      }

      const body = response.data as any;
      if (response.ok && body?.prefix === "2xx") {
        message.success(this.isEdit ? "Credential updated" : "Credential created");
        this.props.closeModal();
      } else {
        // Map field-level validation errors (e.g. { url: ["The url format is invalid."] })
        // onto their inputs, and surface the general reason as a toast.
        if (body?.status === 422) {
          this.applyValidationErrors(body?.error);
        }
        const reason =
          body?.message ||
          response.originalError?.message ||
          response.problem ||
          "unknown error";
        message.error(typeof reason === "string" ? reason : JSON.stringify(reason));
      }
    } catch (e) {
      message.error("Could not save credential");
    } finally {
      this.setState({ saving: false });
    }
  }

  // Attach backend validation errors to the matching form fields. Credential
  // field keys (e.g. "url") live under the "credentials" object, so they are
  // mapped to the ["credentials", key] path; dotted keys are split into a path.
  private applyValidationErrors(errorObj: any): boolean {
    if (!errorObj || typeof errorObj !== "object" || Array.isArray(errorObj)) {
      return false;
    }

    const schema = this.getCredentialsSchema(this.state.partnerKey);
    const credentialKeys = schema ? Object.keys(schema.fields) : [];

    const fields = Object.keys(errorObj).map((key) => {
      let name: (string | number)[];

      if (key.includes(".")) {
        name = key.split(".");
      } else if (credentialKeys.includes(key)) {
        name = ["credentials", key];
      } else {
        name = [key];
      }

      const raw = errorObj[key];
      const errors = Array.isArray(raw) ? raw : [String(raw)];

      return { name, errors };
    });

    if (fields.length > 0) {
      this.form.current?.setFields(fields);
      return true;
    }

    return false;
  }

  // Keep only the credential fields that belong to the selected partner
  private pickCredentials(partnerKey: string, values: any) {
    const schema = this.getCredentialsSchema(partnerKey);
    const credentials: any = {};
    if (schema) {
      Object.keys(schema.fields).forEach((key) => {
        credentials[key] = values.credentials?.[key];
      });
    }
    return credentials;
  }

  renderPartners() {
    return (
      <Form.Item
        label="Partner"
        name="partner_key"
        rules={[{ required: !this.isEdit, message: "Partner is required" }]}
      >
        <Select
          placeholder="Select a partner"
          disabled={this.isEdit}
          showSearch
          allowClear
          onChange={(value: any) => this.setState({ partnerKey: value || "" })}
        >
          {PartnerKeyTypeArray.map((v) => (
            <Select.Option key={v} value={v}>
              {v}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  renderShelters() {
    const { shelters } = this.props;

    return (
      <Form.Item
        label="Shelter"
        name="shelter_uuid"
        rules={[{ required: !this.isEdit, message: "Shelter is required" }]}
      >
        <Select
          placeholder="Select a shelter"
          disabled={this.isEdit}
          showSearch
          allowClear
          filterOption={false}
          loading={this.props.loadingShelters}
          onSearch={(v) => this.delayedSearch(v)}
          onDropdownVisibleChange={(open) => {
            if (open) this.props.loadShelters();
          }}
          onChange={(value: any) => this.setState({ shelterUuid: value })}
        >
          {shelters?.map((v: any) => (
            <Select.Option key={v.uuid} value={v.uuid}>
              {v.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  // Render the credential inputs based on the selected partner_key
  mountFields() {
    const { partnerKey } = this.state;
    if (!partnerKey) return null;

    const schema = this.getCredentialsSchema(partnerKey);
    if (!schema) {
      return (
        <Typography.Text type="secondary">
          This partner has no configurable credential fields.
        </Typography.Text>
      );
    }

    return (
      <Row gutter={24}>
        {Object.keys(schema.fields).map((key) => {
          const field = schema.fields[key];

          return (
            <Col span={12} key={`credential-field-${key}`}>
              <Form.Item
                label={field.label}
                name={["credentials", key]}
                extra={field.description}
                rules={[
                  {
                    required: field.required,
                    message: "Required field",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          );
        })}
      </Row>
    );
  }

  renderCredentialsSection() {
    const { showCredentials, revealing, revealError } = this.state;

    // Edit mode, credentials still hidden: offer to reveal/edit them.
    if (this.isEdit && !showCredentials) {
      return (
        <>
          <Divider />
          {revealError && (
            <Alert
              type="error"
              showIcon
              message={revealError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Button
            icon={<UnlockOutlined />}
            loading={revealing}
            onClick={() => this.revealCredentials()}
          >
            Edit credentials
          </Button>
        </>
      );
    }

    return (
      <>
        <Divider>Credentials</Divider>
        {this.mountFields()}
      </>
    );
  }

  render() {
    const { saving } = this.state;

    return (
      <Form
        ref={this.form}
        name="integration_credential"
        layout="vertical"
        onFinish={this.handleSave.bind(this)}
        initialValues={{
          partner_key: this.props.selectedItem?.partner_key,
          shelter_uuid: this.props.selectedItem?.shelter?.uuid,
          description: this.props.selectedItem?.description,
          credentials: {},
        }}
      >
        <Row gutter={24}>
          <Col span={12}>{this.renderPartners()}</Col>
          <Col span={12}>{this.renderShelters()}</Col>
        </Row>

        <Divider />

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: "Description is required" }]}
        >
          <Input placeholder="Credential description" />
        </Form.Item>

        {this.renderCredentialsSection()}

        <Form.Item style={{ marginTop: 32 }}>
          <Button type="primary" htmlType="submit" loading={saving}>
            {this.isEdit ? "Update" : "Save"}
          </Button>
        </Form.Item>
      </Form>
    );
  }
}
